// QuantitySelector - +/- buttons with input field
import { useState, useEffect, useRef } from 'react'
import { Minus, Plus } from 'lucide-react'

export interface QuantitySelectorProps {
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  multiple?: number  // Step/multiple the value must land on (packs/lengths). Default 1.
  stock?: number  // Available stock - for visual indicator only
  disabled?: boolean
  size?: 'sm' | 'md'
  warning?: boolean
  // Id of an external element describing why the value is invalid. Wired to
  // the input's aria-describedby so screen readers announce the reason
  // alongside the amber warning tint.
  ariaDescribedBy?: string
  // Stretch the stepper to fill its parent. Also swaps the pill shape for a
  // softer rounded rectangle and widens the +/- buttons so the input reads
  // as part of a stepper (not a lonely text field with "1" floating inside).
  fullWidth?: boolean
  // With fullWidth: a slightly shorter stepper (search quick-add card).
  compact?: boolean
}

export function QuantitySelector({
  value,
  onChange,
  min = 1,
  multiple = 1,
  stock,
  disabled = false,
  size = 'md',
  warning = false,
  ariaDescribedBy,
  fullWidth = false,
  compact = false,
}: QuantitySelectorProps) {
  // Products sold in fixed multiples step by `multiple` and can't go below one
  // multiple. multiple === 1 → ordinary 1-by-1 stepping from `min`.
  const step = multiple > 1 ? multiple : 1
  const effectiveMin = multiple > 1 ? multiple : min
  const [displayValue, setDisplayValue] = useState(value === null ? '' : String(value))
  const prevValueRef = useRef(value)

  // Sync display when value changes externally (e.g. branch switch resets quantity)
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value
      setDisplayValue(value === null ? '' : String(value))
    }
  }, [value])

  const isEmpty = displayValue === ''
  const parsedDisplay = isEmpty ? null : parseInt(displayValue, 10)
  const isOverStock = stock !== undefined && parsedDisplay !== null && parsedDisplay > stock

  const inputWidth = (() => {
    const len = displayValue.length || 1
    return (size === 'sm' ? 44 : 56) + Math.max(0, len - 2) * 10
  })()

  const commit = (next: number) => {
    setDisplayValue(String(next))
    prevValueRef.current = next
    onChange(next)
  }

  const handleDecrement = () => {
    if (parsedDisplay === null) return
    // Snap down to the previous multiple (handles off-grid typed values),
    // floored at one multiple.
    const lower =
      step > 1 ? (Math.ceil(parsedDisplay / step) - 1) * step : parsedDisplay - 1
    const next = Math.max(lower, effectiveMin)
    if (next < parsedDisplay) commit(next)
  }

  const handleIncrement = () => {
    if (parsedDisplay === null) {
      commit(effectiveMin)
      return
    }
    // Snap up to the next multiple (handles off-grid typed values).
    const higher =
      step > 1 ? (Math.floor(parsedDisplay / step) + 1) * step : parsedDisplay + 1
    commit(Math.min(Math.max(higher, effectiveMin), 99999))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '') {
      setDisplayValue('')
      prevValueRef.current = null
      onChange(null)
      return
    }
    const parsed = parseInt(raw, 10)
    if (isNaN(parsed)) return
    const clamped = Math.min(Math.max(parsed, min), 99999)
    setDisplayValue(String(clamped))
    prevValueRef.current = clamped
    onChange(clamped)
  }

  const decrementDisabled = disabled || isEmpty || (parsedDisplay !== null && parsedDisplay <= effectiveMin)
  const incrementDisabled = disabled || (parsedDisplay !== null && parsedDisplay >= 99999)

  const smBtn = 'h-9 w-8 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
  // Wider buttons in fullWidth mode so the +/- feel like the primary
  // affordance and don't get visually swamped by the stretched input.
  const fullWidthHeight = compact ? 'h-[41px] sm:h-[45px]' : 'h-11 sm:h-12'
  const mdBtn = fullWidth
    ? `${fullWidthHeight} w-16 sm:w-20 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors`
    : 'h-10 w-9 sm:h-12 sm:w-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'

  return (
    <div
      className={`${fullWidth ? 'flex w-full rounded-xl' : 'inline-flex rounded-full'} items-stretch bg-white border border-gray-200 overflow-hidden`}
    >
      <button
        type="button"
        onClick={handleDecrement}
        disabled={decrementDisabled}
        className={size === 'sm' ? smBtn : mdBtn}
        aria-label="Decrease quantity"
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>

      <input
        type="number"
        min={effectiveMin}
        max={99999}
        step={step}
        value={displayValue}
        onChange={handleInputChange}
        disabled={disabled}
        aria-invalid={warning || undefined}
        aria-describedby={ariaDescribedBy}
        style={
          fullWidth
            ? { outline: 'none', boxShadow: 'none' }
            : { width: `${inputWidth}px`, outline: 'none', boxShadow: 'none' }
        }
        className={`text-center font-semibold tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ${fullWidth ? 'flex-1 min-w-0 border-y-0 border-l border-r border-gray-100 focus:border-gray-100 focus-visible:border-gray-100' : 'border-0'} ${size === 'sm' ? 'text-xs' : 'text-xs sm:text-sm'} ${
          isOverStock
            ? 'bg-gray-50 text-amber-700'
            : warning
              ? 'bg-amber-50 text-amber-900'
              : 'bg-gray-50 text-gray-900'
        }`}
      />

      <button
        type="button"
        onClick={handleIncrement}
        disabled={incrementDisabled}
        className={size === 'sm' ? smBtn : mdBtn}
        aria-label="Increase quantity"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  )
}
